import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy } from '@angular/core';
import { Observable, ReplaySubject, Subject, takeUntil } from 'rxjs';

import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';

import { DotCmsFacade } from '../../../facades/dot-cms.facade';
import { CustomDotCmsContentlet } from '../../../models/contentlet/custom-dotcms-contentlet.model';
import { setupIntersectionObserver } from '../../../utils/dot-cms-utils';

@Component({
  selector: 'custom-dot-cms-banner',
  templateUrl: './dot-cms-banner.component.html',
  styleUrls: ['./dot-cms-banner.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DotCmsBannerComponent implements OnChanges, OnDestroy {
  imageUrl = new ReplaySubject<string>(1);

  @Input() identifier: string;
  contentlet$ = new ReplaySubject<CustomDotCmsContentlet>(1);
  animationTriggered$ = new ReplaySubject<boolean>(1);
  intersectionObserver: IntersectionObserver;
  contentlet: CustomDotCmsContentlet;
  private destroy$ = new Subject<void>();
  triggerCount = 1;
  constructor(
    private dotCmsFacade: DotCmsFacade,
    private elementRef: ElementRef,
    private gtmFacade: GoogleTagManagerFacade
  ) {}
  ngOnChanges() {
    this.dotCmsFacade
      .getContentletByIdentifier$(this.identifier)
      .pipe(takeUntil(this.destroy$))
      .subscribe(contentlet => {
        this.contentlet = contentlet;
        this.contentlet$.next(contentlet);
        this.intersectionObserver = setupIntersectionObserver(
          this.elementRef.nativeElement,
          this.animationTriggered$,
          contentlet.showAnimation
        );
      });
    this.contentlet$.pipe(takeUntil(this.destroy$)).subscribe(contentlet => {
      this.gtmFacade.setupIntersectionObserverForGTMEvent(
        this.elementRef.nativeElement,
        [contentlet],
        this.triggerCount,
        'view_promotion'
      );
    });
  }

  /* layout styling methods starts here*/
  getColumnCss(layout: string): string {
    switch (layout) {
      case '1': {
        return 'col-lg-9 text-center';
      }
      case '2': {
        return 'col-lg-7 text-center';
      }
      case '3': {
        return 'text-center';
      }
      default: {
        return 'col-lg-7 text-center';
      }
    }
  }
  getRowCss(layout: string): string {
    switch (layout) {
      case '1': {
        return 'row justify-content-center';
      }
      case '2': {
        return 'row justify-content-start';
      }
      case '3': {
        return 'banner-three-wrapper';
      }
      default: {
        return 'row justify-content-end';
      }
    }
  }

  getBannerCss(layout: string): string {
    switch (layout) {
      case '1': {
        return 'caption-banner';
      }
      case '2': {
        return 'caption-banner dot-cms-banner';
      }
      case '3': {
        return 'caption-banner';
      }
      default: {
        return 'caption-banner dot-cms-banner-four';
      }
    }
  }
  /* layout styling methods ends here*/

  getImageSource(contentlet: CustomDotCmsContentlet): Observable<string> {
    return this.dotCmsFacade.getBannerImageURI(contentlet);
  }
  bannerOverlay(contentlet: CustomDotCmsContentlet): string {
    const bannerContent = contentlet?.buttonText || contentlet?.link || contentlet?.bannerTitle || contentlet?.caption;
    if (bannerContent) {
      return 'banner-overlay';
    }
    return;
  }
  bannerWidth(contentlet: CustomDotCmsContentlet): string {
    if (contentlet?.maxWidth) {
      return 'banner-width';
    }
    return;
  }

  ngOnDestroy() {
    this.intersectionObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  captionBackgroundHide(contentlet: CustomDotCmsContentlet, layout: string): string {
    const content = contentlet?.buttonText || contentlet?.link || contentlet?.bannerTitle || contentlet?.caption;

    if (!content && (layout === '2' || layout === '4')) {
      return 'caption-background-hide';
    }

    return null;
  }
}
